import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  WEGNAME,
  entscheiden,
  erfassen,
  widerrufe as ladeWiderrufe,
  type Widerruf,
  type Widerrufsweg,
} from '../api/widerrufe';
import { markenklasse } from '../marken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Der Widerruf im Haus (LEG-15 bis LEG-17, Paket 8 G).
 *
 * DIE ARBEITSLISTE: jeder Widerruf ohne Entscheidung oder ohne versandte
 * Eingangsbestätigung, das älteste oben. Je Zeile das Recht, wie es für die
 * Buchung gerechnet wird (LEG-16), und wie viele Tage der Frist beim Zugang
 * noch liefen (LEG-17).
 *
 * ERFASSEN: Ein Widerruf per Mail oder Brief wird mit dem Zeitpunkt
 * eingetragen, zu dem er ZUGING — nicht mit dem der Erfassung.
 *
 * ENTSCHEIDEN: „Anerkennen" storniert die Buchung ohne Gebühr, die Erstattung
 * läuft über den Storno (LEG-17). „Ablehnen" verlangt einen Grund. Beides
 * einmal und endgültig.
 *
 * SEITENTYP LISTE (ÄA-41): Zähler im Seitenkopf, „offen · alle" in der
 * Filterleiste, die Tabelle in der Karte. Die Erfassung steht in der
 * Seitenleiste daneben — sie ist die zweite Arbeit der Maske und braucht die
 * Tabelle nicht. Die Entscheidung bleibt in der Zeile: Sie gehört zu genau
 * einem Widerruf und verlangt dort ihren Grund.
 */

function zeit(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

function rechtsmarke(w: Widerruf): JSX.Element {
  const r = w.widerrufsrecht;
  if (r.besteht === null) return <span className={markenklasse('aufmerksamkeit')}>ungeklärt</span>;
  if (!r.besteht) return <span className={markenklasse('stillgelegt')}>kein Widerrufsrecht</span>;
  if (r.erloschen) return <span className={markenklasse('stillgelegt')}>erloschen</span>;
  /* LEG-17: die versäumte Frist in Achtung — über sie ist zu entscheiden. */
  if (w.restTageBeimZugang !== null && w.restTageBeimZugang < 0) {
    return <span className={markenklasse('aufmerksamkeit')}>nach Fristende zugegangen</span>;
  }
  return <span className={markenklasse('abschluss')}>in der Frist</span>;
}

type Erfassung = {
  buchungsnummer: string;
  weg: Exclude<Widerrufsweg, 'widerrufsseite'>;
  zugangAm: string;
  email: string;
  erklaerung: string;
};

const LEER: Erfassung = { buchungsnummer: '', weg: 'mail', zugangAm: '', email: '', erklaerung: '' };

export function Widerrufe({
  darfBearbeiten,
  beiBuchung,
}: {
  /** `buchung:stornieren` — die Anerkennung ist ein Storno. */
  readonly darfBearbeiten: boolean;
  readonly beiBuchung: (buchungsnummer: string) => void;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Widerruf[] | null>(null);
  const [alle, setzeAlle] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [erfassung, setzeErfassung] = useState<Erfassung>(LEER);
  const [gruende, setzeGruende] = useState<Readonly<Record<string, string>>>({});
  /* CRM-103: die Entscheidung, nach der das Pop-up gerade fragt. */
  const [nachfrage, setzeNachfrage] = useState<{ readonly w: Widerruf; readonly anerkannt: boolean } | null>(
    null,
  );

  const holen = useCallback(async () => {
    try {
      setzeListe(await ladeWiderrufe(alle));
      setzeFehler(null);
    } catch (f) {
      setzeListe([]);
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, [alle]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * Wirft beim Schreiben (CRM-103): Aus der Rückfrage heraus steht der Fehler
   * im Pop-up, und es bleibt offen. `tue` fängt ihn für die Erfassung und
   * schreibt ihn auf die Seite.
   */
  const schreibe = useCallback(
    async (was: () => Promise<string>) => {
      setzeLaeuft(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const text = await was();
        setzeNachfrage(null);
        setzeMeldung(text);
        await holen();
      } finally {
        setzeLaeuft(false);
      }
    },
    [holen],
  );

  const tue = useCallback(
    async (was: () => Promise<string>) => {
      try {
        await schreibe(was);
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      }
    },
    [schreibe],
  );

  const entscheide = (w: Widerruf, anerkannt: boolean): Promise<void> => {
    const grund = (gruende[w.id] ?? '').trim();
    return schreibe(async () => {
      await entscheiden(w.id, { anerkannt, grund });
      setzeGruende((alt) => ({ ...alt, [w.id]: '' }));
      return anerkannt
        ? `Widerruf zu ${w.buchungsnummer} anerkannt — die Buchung ist ohne Gebühr storniert.`
        : `Widerruf zu ${w.buchungsnummer} abgelehnt.`;
    });
  };

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(liste === null ? undefined : zaehlwort(liste.length, 'Widerruf', 'Widerrufe'));

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        vorschauBeschriftung="Widerruf erfassen"
        filter={
          <Segmente
            beschriftung="Umfang"
            gewaehlt={alle ? 'alle' : 'offen'}
            beiWahl={(wert) => {
              setzeAlle(wert === 'alle');
            }}
            optionen={[
              { wert: 'offen', text: 'offen' },
              { wert: 'alle', text: 'alle' },
            ]}
          />
        }
        hinweise={
          <>
            <p className="hilfe">
              Widerrufe, über die noch nicht entschieden ist oder deren Eingangsbestätigung noch
              aussteht. Anerkennen storniert die Buchung ohne Gebühr; die Erstattung läuft über den
              Storno.
            </p>
            {meldung !== null && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
          </>
        }
        vorschau={
          darfBearbeiten ? (
            <section aria-label="Widerruf erfassen">
              <div className="karte__titel">
                <h2>Widerruf per Mail oder Brief erfassen</h2>
              </div>
              {/* LEG-17: erfasst wird der Zugang, nicht die Erfassung. */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void tue(async () => {
                    const { bestaetigt } = await erfassen({
                      buchungsnummer: erfassung.buchungsnummer.trim(),
                      weg: erfassung.weg,
                      zugangAm: new Date(erfassung.zugangAm).toISOString(),
                      email: erfassung.email.trim(),
                      erklaerung:
                        erfassung.erklaerung.trim() === '' ? null : erfassung.erklaerung.trim(),
                    });
                    setzeErfassung(LEER);
                    return bestaetigt
                      ? 'Widerruf erfasst; die Eingangsbestätigung ist eingereiht.'
                      : 'Widerruf erfasst; die Eingangsbestätigung wird nachgeholt.';
                  });
                }}
              >
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="wr-nummer">
                    Buchungsnummer
                  </label>
                  <input
                    id="wr-nummer"
                    required
                    value={erfassung.buchungsnummer}
                    onChange={(e) => setzeErfassung({ ...erfassung, buchungsnummer: e.target.value })}
                  />
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="wr-weg">
                    Eingegangen per
                  </label>
                  <select
                    id="wr-weg"
                    value={erfassung.weg}
                    onChange={(e) => setzeErfassung({ ...erfassung, weg: e.target.value as Erfassung['weg'] })}
                  >
                    {(['mail', 'brief', 'telefonisch_nachgereicht'] as const).map((w) => (
                      <option key={w} value={w}>
                        {WEGNAME[w]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="wr-zugang">
                    Zugegangen am
                  </label>
                  <input
                    id="wr-zugang"
                    type="datetime-local"
                    required
                    value={erfassung.zugangAm}
                    onChange={(e) => setzeErfassung({ ...erfassung, zugangAm: e.target.value })}
                  />
                  <p className="feld__hilfe">Wann die Erklärung einging — nicht, wann sie erfasst wird.</p>
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="wr-email">
                    Bestätigung an
                  </label>
                  <input
                    id="wr-email"
                    type="email"
                    required
                    value={erfassung.email}
                    onChange={(e) => setzeErfassung({ ...erfassung, email: e.target.value })}
                  />
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="wr-erklaerung">
                    Wortlaut der Erklärung
                  </label>
                  <textarea
                    id="wr-erklaerung"
                    rows={3}
                    value={erfassung.erklaerung}
                    onChange={(e) => setzeErfassung({ ...erfassung, erklaerung: e.target.value })}
                  />
                </div>
                <div className="knopfreihe">
                  <button type="submit" className="knopf knopf--klein" disabled={laeuft}>
                    Widerruf erfassen
                  </button>
                </div>
              </form>
            </section>
          ) : undefined
        }
      >
        {liste === null && <p role="status">Wird geladen …</p>}
        {liste !== null && liste.length === 0 && (
          <p className="liste__leer">
            {alle ? 'Es ist noch kein Widerruf eingegangen.' : 'Kein Widerruf ist offen.'}
          </p>
        )}

        {liste !== null && liste.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Widerrufe">
              <thead>
                <tr>
                  <th scope="col">Buchung</th>
                  <th scope="col">Leistung</th>
                  <th scope="col">Zugang</th>
                  <th scope="col">Widerrufsrecht</th>
                  <th scope="col">Bestätigung</th>
                  <th scope="col">Entscheidung</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((w) => (
                  <tr key={w.id}>
                    <th scope="row">
                      <button
                        type="button"
                        className="liste__oeffnen liste__nummer"
                        onClick={() => {
                          beiBuchung(w.buchungsnummer);
                        }}
                      >
                        {w.buchungsnummer}
                      </button>
                      <br />
                      <span className="hilfe">{w.kunde}</span>
                    </th>
                    <td>
                      {w.leistung}
                      <br />
                      <span className="hilfe liste__einzeilig">{zeit(w.beginn)}</span>
                    </td>
                    <td>
                      <span className="liste__einzeilig">{zeit(w.zugangAm)}</span>
                      <br />
                      <span className="hilfe">{WEGNAME[w.weg]}</span>
                      {w.erklaerung !== null && (
                        <>
                          <br />
                          <span className="hilfe">„{w.erklaerung}"</span>
                        </>
                      )}
                    </td>
                    <td>
                      {rechtsmarke(w)}
                      <br />
                      <span className="hilfe">{w.widerrufsrecht.grund}</span>
                      {w.restTageBeimZugang !== null && w.restTageBeimZugang >= 0 && (
                        <>
                          <br />
                          <span className="hilfe">beim Zugang noch {w.restTageBeimZugang} Tage Frist</span>
                        </>
                      )}
                    </td>
                    <td>
                      {w.bestaetigtAm === null ? (
                        <span className={markenklasse('aufmerksamkeit')}>ausstehend</span>
                      ) : (
                        <span className="hilfe liste__einzeilig">{zeit(w.bestaetigtAm)}</span>
                      )}
                    </td>
                    <td>
                      {w.entscheidung !== null ? (
                        <>
                          <span
                            className={markenklasse(
                              w.entscheidung === 'anerkannt' ? 'abschluss' : 'stillgelegt',
                            )}
                          >
                            {w.entscheidung}
                          </span>
                          <br />
                          <span className="hilfe">{w.entscheidungsgrund}</span>
                        </>
                      ) : darfBearbeiten ? (
                        <>
                          <label className="feld__beschriftung" htmlFor={`wr-grund-${w.id}`}>
                            Begründung
                          </label>
                          <input
                            id={`wr-grund-${w.id}`}
                            value={gruende[w.id] ?? ''}
                            onChange={(e) => setzeGruende((alt) => ({ ...alt, [w.id]: e.target.value }))}
                          />
                          <div className="knopfreihe">
                            <button
                              type="button"
                              className="knopf knopf--klein knopf--eingriff"
                              disabled={laeuft || (gruende[w.id] ?? '').trim() === ''}
                              onClick={() => setzeNachfrage({ w, anerkannt: true })}
                            >
                              Anerkennen und stornieren …
                            </button>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={laeuft || (gruende[w.id] ?? '').trim() === ''}
                              onClick={() => setzeNachfrage({ w, anerkannt: false })}
                            >
                              Ablehnen …
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className={markenklasse('aufmerksamkeit')}>offen</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {/*
        LEG-17: Die Begründung steht in der Zeile und wird hier nur genannt.
        Die Entscheidung fällt einmal und endgültig.
      */}
      {nachfrage !== null && (
        <Rueckfrage
          frage={nachfrage.anerkannt ? 'Widerruf anerkennen?' : 'Widerruf ablehnen?'}
          unterzeile={`${nachfrage.w.buchungsnummer} · ${nachfrage.w.kunde}`}
          tat={nachfrage.anerkannt ? 'Anerkennen und stornieren' : 'Ablehnen'}
          beiBestaetigen={() => entscheide(nachfrage.w, nachfrage.anerkannt)}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        >
          <p>
            {nachfrage.w.leistung} am {zeit(nachfrage.w.beginn)}, Widerruf zugegangen am{' '}
            {zeit(nachfrage.w.zugangAm)}.{' '}
            {nachfrage.anerkannt
              ? 'Die Buchung wird ohne Gebühr storniert; die Erstattung läuft über den Storno.'
              : 'Die Buchung bleibt bestehen.'}{' '}
            Die Entscheidung ist endgültig.
          </p>
          <p>Begründung: {(gruende[nachfrage.w.id] ?? '').trim()}</p>
        </Rueckfrage>
      )}
    </>
  );
}
